import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/lib/auth.jsx';
import { LocaleProvider } from '@/lib/locale';
import { BookingLanguageBar } from '@/components/regional/LanguageSwitcher';
import { FeatureFlagProvider } from '@/lib/featureFlags';
import { Toaster } from 'sonner';
import { RouteGuard } from '@/components/RouteGuard';
import { SiteAnalyticsTracker } from '@/components/SiteAnalyticsTracker';
// ChatGPT Ads measurement pixel — marketing routes only (see lib/adsPixel.js).
import { AdsPixelTracker } from '@/components/AdsPixelTracker';
import '@/App.css';

import SplashScreen from '@/components/SplashScreen';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import TwoFactorVerify from '@/pages/TwoFactorVerify';
import Signup from '@/pages/Signup';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import CoachPublic from '@/pages/CoachPublic';
import ParentRegistration from '@/pages/ParentRegistration';
import CartCheckout from '@/pages/CartCheckout';
import EnrollmentInvitePage from '@/pages/parent/EnrollmentInvitePage';
import PayInvoicePage from '@/pages/parent/PayInvoicePage';
import Blog from '@/pages/blog/Blog';
import BlogPost from '@/pages/blog/BlogPost';

const InstructorOnboarding = lazy(() => import('@/pages/instructor/Onboarding'));
const InstructorLayout = lazy(() => import('@/pages/instructor/InstructorLayout'));
const InstructorDashboard = lazy(() => import('@/pages/instructor/Dashboard'));
const InstructorApplications = lazy(() => import('@/pages/instructor/Applications'));
const InstructorLandingPage = lazy(() => import('@/pages/instructor/LandingPageEditor'));
const InstructorLandingPageVisualEditor = lazy(() => import('@/pages/instructor/LandingPageVisualEditor'));
const InstructorStudents = lazy(() => import('@/pages/instructor/Students'));
const InstructorInvoices = lazy(() => import('@/pages/instructor/Invoices'));
const InstructorBookkeeping = lazy(() => import('@/pages/instructor/Bookkeeping'));
const InstructorPayments = lazy(() => import('@/pages/instructor/Payments'));
const InstructorLocations = lazy(() => import('@/pages/instructor/Locations'));
const InstructorPackages = lazy(() => import('@/pages/instructor/Packages'));
const RosterPrint = lazy(() => import('@/pages/instructor/RosterPrint'));
const ScheduleDayPrint = lazy(() => import('@/pages/instructor/ScheduleDayPrint'));
const ScheduleRangePrint = lazy(() => import('@/pages/instructor/ScheduleRangePrint'));
const SetupQuiz = lazy(() => import('@/pages/instructor/SetupQuiz'));
const InstructorDiscountCodes = lazy(() => import('@/pages/instructor/DiscountCodes'));
const InstructorWaivers = lazy(() => import('@/pages/instructor/Waivers'));
const InstructorSettings = lazy(() => import('@/pages/instructor/Settings'));
// Phase 64 — InstructorAvailability deleted. Calendar sync (the only
// useful piece) moved to the Schedule page header. The backend
// /api/instructor/availability endpoints are kept intact so the
// CalendarSyncDialog can still fetch the iCal feed URL.
const InstructorSchedule = lazy(() => import('@/pages/instructor/Schedule'));
const InstructorRoute = lazy(() => import('@/pages/instructor/Route'));
const OfferPage = lazy(() => import('@/pages/OfferPage'));
const InstructorBilling = lazy(() => import('@/pages/instructor/Billing'));
const InstructorMarketing = lazy(() => import('@/pages/instructor/Marketing'));
const InstructorOnboardingCall = lazy(() => import('@/pages/instructor/OnboardingCall'));
const InstructorFamilyCare = lazy(() => import('@/pages/instructor/FamilyCare'));
const ParentProgress = lazy(() => import('@/pages/parent/Progress'));
const ProgressPrint = lazy(() => import('@/pages/parent/ProgressPrint'));
const InstructorBroadcast = lazy(() => import('@/pages/instructor/Broadcast'));
const InstructorStaff = lazy(() => import('@/pages/instructor/Staff'));
const InstructorImportFamilies = lazy(() => import('@/pages/instructor/ImportFamilies'));
import ClaimFamily from '@/pages/ClaimFamily';

const StaffLayout = lazy(() => import('@/pages/staff/StaffLayout'));
const StaffDashboard = lazy(() => import('@/pages/staff/StaffDashboard'));
const StaffSchedule = lazy(() => import('@/pages/staff/StaffSchedule'));
const StaffAvailability = lazy(() => import('@/pages/staff/StaffAvailability'));
const StaffStudents = lazy(() => import('@/pages/staff/StaffStudents'));
const StaffShifts = lazy(() => import('@/pages/staff/StaffShifts'));
const StaffTimeclock = lazy(() => import('@/pages/staff/StaffTimeclock'));
const StaffSecurity = lazy(() => import('@/pages/staff/StaffSecurity'));
const StaffActivate = lazy(() => import('@/pages/staff/StaffActivate'));

const ParentLayout = lazy(() => import('@/pages/parent/ParentLayout'));
const ParentDashboard = lazy(() => import('@/pages/parent/Dashboard'));
const ParentCoachPortal = lazy(() => import('@/pages/parent/CoachPortal'));
const ParentBookLessons = lazy(() => import('@/pages/parent/BookLessons'));
const ParentProfile = lazy(() => import('@/pages/parent/Profile'));
const ParentEnrollments = lazy(() => import('@/pages/parent/Enrollments'));
const ParentPayments = lazy(() => import('@/pages/parent/Payments'));
const ParentMyStudents = lazy(() => import('@/pages/parent/MyStudents'));
const ParentSchedule = lazy(() => import('@/pages/parent/Schedule'));
const ParentPolicies = lazy(() => import('@/pages/parent/Policies'));
import FindInstructor from '@/pages/FindInstructor';

const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const AdminInstructors = lazy(() => import('@/pages/admin/Instructors'));
const AdminInstructorDetail = lazy(() => import('@/pages/admin/InstructorDetail'));
const AdminStripeWebhooks = lazy(() => import('@/pages/admin/StripeWebhooks'));
const AdminDiscountCodes = lazy(() => import('@/pages/admin/DiscountCodes'));
const AdminCancellationReasons = lazy(() => import('@/pages/admin/CancellationReasons'));
const AdminAuditLog = lazy(() => import('@/pages/admin/AuditLog'));

const AdminMassEmail = lazy(() => import('@/pages/admin/MassEmail'));
const AdminSecurity = lazy(() => import('@/pages/admin/Security'));
const AdminDunning = lazy(() => import('@/pages/admin/Dunning'));
const AdminAnalytics = lazy(() => import('@/pages/admin/Analytics'));
// Scaling Mission — Live Latency Panel (per-route p95 health page).
const AdminHealth = lazy(() => import('@/pages/admin/Health'));
const AdminFeatureFlags = lazy(() => import('@/pages/admin/FeatureFlagsAdmin'));
const AdminPaymentHeal = lazy(() => import('@/pages/admin/PaymentHeal'));
const AdminAIAssistant = lazy(() => import('@/pages/admin/AIAssistant'));
const AdminFraudReview = lazy(() => import('@/pages/admin/FraudReview'));
const AdminSubscriptionPricing = lazy(() => import('@/pages/admin/SubscriptionPricing'));
// Phase 93 — coach testimonial moderation (hide/show published reviews).
const AdminTestimonials = lazy(() => import('@/pages/admin/Testimonials'));
const AdminBlog = lazy(() => import('@/pages/admin/Blog'));
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Security from '@/pages/Security';
import SmsTerms from '@/pages/SmsTerms';
import Accessibility from '@/pages/Accessibility';
import DataDeletionRequest from '@/pages/DataDeletionRequest';

// Phase 0b1 — AI-discoverability / LLM SEO landing pages.
// Public, no-auth pages that give crawlers and LLMs clear,
// well-structured content for high-intent search terms.
import BestSwimSchoolSoftware from '@/pages/seo/BestSwimSchoolSoftware';
import SwimLessonRegistrationSoftware from '@/pages/seo/SwimLessonRegistrationSoftware';
import ForSwimInstructors from '@/pages/seo/ForSwimInstructors';
import VsJackrabbitSwim from '@/pages/seo/VsJackrabbitSwim';
// Phase 0b3 — competitor comparison pages (config-driven).
import VsIClassPro from '@/pages/seo/VsIClassPro';
import VsMindbody from '@/pages/seo/VsMindbody';
import VsSwimTopia from '@/pages/seo/VsSwimTopia';
import VsTeamUnify from '@/pages/seo/VsTeamUnify';
// Phase 0b5 — high-value 2026 competitors surfaced by Wingman.
// Swum.ai gets the "flat $0.25 per transaction" wedge + Coach Profit Calculator;
// Acuity gets the "purpose-built vs generic booking calendar" wedge.
import VsSwumAi from '@/pages/seo/VsSwumAi';
import VsAcuity from '@/pages/seo/VsAcuity';
// Phase 0b8 — dedicated ad-conversion landing page. Bundled statically
// with the rest of the marketing routes so paid-ad first-paint is fast
// and prerender-seo.mjs can crawl it.
import Switch from '@/pages/Switch';
// Phase 92 — site-audit marketing pages (heycatch.ai fixes D5.1 + D3.3).
// Dedicated /about, /pricing, /features, /customers routes with their own
// titles + meta so they no longer fall through the SPA catch-all to the
// homepage. Statically bundled like the other marketing routes.
import About from '@/pages/About';
import PricingPage from '@/pages/PricingPage';
import FeaturesPage from '@/pages/FeaturesPage';
import CustomersPage from '@/pages/CustomersPage';

function App() {
    // Phase 153 — the inline first-byte splash in public/index.html hands
    // off to React here. Our Suspense fallback (SplashScreen) looks identical,
    // so the swap is invisible; the HTML copy is then removed from the DOM.
    useEffect(() => {
        if (typeof window.__spHideSplash === 'function') window.__spHideSplash();
    }, []);
    return (
        <div className="App">
                <AuthProvider>
                    <FeatureFlagProvider>
                        <BrowserRouter>
                    <LocaleProvider>
                    <BookingLanguageBar />
                    <Toaster position="top-right" richColors closeButton />
                    {/* Phase 122 — first-party, cookie-free page-view beacon (public pages only). */}
                    <SiteAnalyticsTracker />
                    <AdsPixelTracker />
                    <Suspense fallback={<SplashScreen />}>
                        <Routes>
                            <Route path="/" element={<Landing />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/login/2fa" element={<TwoFactorVerify />} />
                            <Route path="/signup" element={<Signup />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/reset-password" element={<ResetPassword />} />
                            <Route path="/coach/:slug" element={<CoachPublic />} />
                            <Route path="/offer/:token" element={<OfferPage />} />
                            {/* Phase 113 — Pro multi-page website subpages (about/programs/
                                locations/contact). Static routes below (register, enroll-cart)
                                outrank this dynamic segment in React Router v6, so existing
                                flows are unaffected. */}
                            <Route path="/coach/:slug/:sitePage" element={<CoachPublic />} />
                            <Route path="/coach/:slug/register" element={<ParentRegistration />} />
                            {/* Phase 103b+c — Class Finder mixed-cart one-checkout */}
                            <Route path="/coach/:slug/enroll-cart" element={<CartCheckout />} />
                            {/* Public enrollment invite page — parent lands here from an
                                email link when their coach enrolled them but they don't
                                have a card on file. No auth: security is via the token. */}
                            <Route path="/parent/invite/:token" element={<EnrollmentInvitePage />} />
                            {/* Phase 42.4 — public invoice pay page (tokenized) */}
                            <Route path="/pay-invoice/:invoiceId" element={<PayInvoicePage />} />
                            {/* Phase 61 — Resource Hub (SEO blog) */}
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/security" element={<Security />} />
                            {/* Phase 92 — dedicated marketing pages (site audit D5.1). */}
                            <Route path="/about" element={<About />} />
                            <Route path="/pricing" element={<PricingPage />} />
                            <Route path="/features" element={<FeaturesPage />} />
                            <Route path="/customers" element={<CustomersPage />} />
                            <Route path="/sms-terms" element={<SmsTerms />} />
                            <Route path="/accessibility" element={<Accessibility />} />
                            <Route path="/privacy/delete-my-data" element={<DataDeletionRequest />} />

                            {/* Phase 0b1 — SEO / LLM discoverability landing pages */}
                            <Route path="/best-swim-school-software" element={<BestSwimSchoolSoftware />} />
                            <Route path="/swim-lesson-registration-software" element={<SwimLessonRegistrationSoftware />} />
                            <Route path="/for-swim-instructors" element={<ForSwimInstructors />} />
                            <Route path="/vs/jackrabbit-swim" element={<VsJackrabbitSwim />} />
                            {/* Phase 0b3 — competitor comparison pages */}
                            <Route path="/vs/iclasspro" element={<VsIClassPro />} />
                            <Route path="/vs/mindbody" element={<VsMindbody />} />
                            <Route path="/vs/swimtopia" element={<VsSwimTopia />} />
                            <Route path="/vs/teamunify" element={<VsTeamUnify />} />
                            {/* Phase 0b5 — Swum.ai (with embedded Coach Profit
                                Calculator on the ComparisonPage) and Acuity. */}
                            <Route path="/vs/swum-ai" element={<VsSwumAi />} />
                            <Route path="/vs/acuity" element={<VsAcuity />} />
                            {/* SEO audit fix — the prerenderer used to emit an orphan
                                duplicate at /vs/acuity-scheduling; permanent client
                                redirect preserves any already-indexed links. */}
                            <Route path="/vs/acuity-scheduling" element={<Navigate to="/vs/acuity" replace />} />
                            {/* Phase 0b8 — dedicated conversion landing page
                                for paid-ad campaigns and Skool community
                                sharing. Single-purpose funnel: hero → savings
                                calculator → live coach roster → SSU discount
                                bridge → final CTA. */}
                            <Route path="/switch" element={<Switch />} />

                            <Route path="/instructor/onboarding" element={<RouteGuard roles={['instructor']}><InstructorOnboarding /></RouteGuard>} />
                            {/* Authenticated preview of the Pro landing page — full-screen, no InstructorLayout chrome */}
                            <Route path="/instructor/landing-page/preview" element={<RouteGuard roles={['instructor']}><CoachPublic /></RouteGuard>} />
                            {/* Phase 106 — "Preview as Parent" now renders the PRIVATE Parent Portal
                                (what an approved parent sees), not the public landing page. Loaded
                                inside the PreviewAsParent overlay iframe; no layout chrome. */}
                            <Route path="/instructor/portal-preview" element={<RouteGuard roles={['instructor']}><ParentCoachPortal previewMode /></RouteGuard>} />
                            {/* Phase 74r — printable roster view is intentionally OUTSIDE InstructorLayout so the sidebar/nav don't render on the print sheet */}
                            <Route path="/instructor/roster-print/:packageId" element={<RouteGuard roles={['instructor']}><RosterPrint /></RouteGuard>} />
                            {/* Phase 74s — one-day multi-class combined roster print (poolside clipboard) */}
                            <Route path="/instructor/schedule-print/:date" element={<RouteGuard roles={['instructor']}><ScheduleDayPrint /></RouteGuard>} />
                            {/* Phase 74t — Week / Month packet: same layout as Day, one page per day with page breaks */}
                            <Route path="/instructor/schedule-print-week/:startDate" element={<RouteGuard roles={['instructor']}><ScheduleRangePrint mode="week" /></RouteGuard>} />
                            <Route path="/instructor/schedule-print-month/:yearMonth" element={<RouteGuard roles={['instructor']}><ScheduleRangePrint mode="month" /></RouteGuard>} />
                            {/* Phase 74u — Setup Quiz (guided smart-setup wizard) */}
                            <Route path="/instructor/setup-quiz" element={<RouteGuard roles={['instructor']}><SetupQuiz /></RouteGuard>} />
                            <Route path="/instructor" element={<RouteGuard roles={['instructor']}><InstructorLayout /></RouteGuard>}>
                                <Route index element={<Navigate to="/instructor/dashboard" replace />} />
                                <Route path="dashboard" element={<InstructorDashboard />} />
                                <Route path="applications" element={<InstructorApplications />} />
                                <Route path="landing-page" element={<InstructorLandingPage />} />
                                {/* Phase 66 — Visual drag-and-drop landing page editor (beta). */}
                                <Route path="landing-page/editor" element={<InstructorLandingPageVisualEditor />} />
                                <Route path="students" element={<InstructorStudents />} />
                                <Route path="import-families" element={<InstructorImportFamilies />} />
                                <Route path="invoices" element={<InstructorInvoices />} />
                                <Route path="payments" element={<InstructorPayments />} />
                                <Route path="bookkeeping" element={<InstructorBookkeeping />} />
                                <Route path="books" element={<Navigate to="/instructor/bookkeeping" replace />} />
                                <Route path="locations" element={<InstructorLocations />} />
                                <Route path="packages" element={<InstructorPackages />} />
                                <Route path="discount-codes" element={<InstructorDiscountCodes />} />
                                {/* Phase 103e — Programs moved INTO the Classes & Packages
                                    page (owner decision: no separate sidebar tab). Old links
                                    land on the Programs tab there. */}
                                <Route path="programs" element={<Navigate to="/instructor/packages?tab=programs" replace />} />
                                <Route path="waivers" element={<InstructorWaivers />} />
                                <Route path="staff" element={<InstructorStaff />} />
                                <Route path="shifts" element={<Navigate to="/instructor/staff?tab=shifts" replace />} />
                                <Route path="timeclock" element={<Navigate to="/instructor/staff?tab=timeclock" replace />} />
                                <Route path="family-care" element={<InstructorFamilyCare />} />
                                <Route path="broadcast" element={<InstructorBroadcast />} />
                                <Route path="settings" element={<InstructorSettings />} />
                                <Route path="schedule" element={<InstructorSchedule />} />
                                <Route path="route" element={<InstructorRoute />} />
                                <Route path="billing" element={<InstructorBilling />} />
                                <Route path="marketing" element={<InstructorMarketing />} />
                                <Route path="onboarding-call" element={<InstructorOnboardingCall />} />
                            </Route>

                            <Route path="/parent/progress/:reportId/print" element={<RouteGuard roles={['parent']}><ProgressPrint /></RouteGuard>} />
                            <Route path="/instructor/progress/:reportId/print" element={<RouteGuard roles={['instructor']}><ProgressPrint coachMode /></RouteGuard>} />
                            <Route path="/parent" element={<RouteGuard roles={['parent']}><ParentLayout /></RouteGuard>}>
                                <Route index element={<Navigate to="/parent/dashboard" replace />} />
                                <Route path="dashboard" element={<ParentDashboard />} />
                                <Route path="schedule" element={<ParentSchedule />} />
                                <Route path="policies" element={<ParentPolicies />} />
                                <Route path="coach" element={<ParentCoachPortal />} />
                                <Route path="progress" element={<ParentProgress />} />
                                <Route path="enrollments" element={<ParentEnrollments />} />
                                <Route path="students" element={<ParentMyStudents />} />
                                <Route path="payments" element={<ParentPayments />} />
                                <Route path="coach/:slug/book" element={<ParentBookLessons />} />
                                <Route path="profile" element={<ParentProfile />} />
                            </Route>
                            {/* Public discovery page — accessible logged-in or logged-out so
                                parents can browse other coaches without losing their session
                                and friends can share the URL freely. */}
                            <Route path="/find-instructor" element={<FindInstructor />} />

                            <Route path="/staff/activate" element={<StaffActivate />} />
                            <Route path="/staff" element={<RouteGuard roles={['staff_coach']}><StaffLayout /></RouteGuard>}>
                                <Route index element={<Navigate to="/staff/dashboard" replace />} />
                                <Route path="dashboard" element={<StaffDashboard />} />
                                <Route path="schedule" element={<StaffSchedule />} />
                                <Route path="availability" element={<StaffAvailability />} />
                                <Route path="students" element={<StaffStudents />} />
                                <Route path="shifts" element={<StaffShifts />} />
                                <Route path="timeclock" element={<StaffTimeclock /> } />
                                <Route path="security" element={<StaffSecurity />} />
                            </Route>

                            <Route path="/admin" element={<RouteGuard roles={['admin']}><AdminLayout /></RouteGuard>}>
                                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                                <Route path="dashboard" element={<AdminDashboard />} />
                                <Route path="instructors" element={<AdminInstructors />} />
                                <Route path="instructors/:id" element={<AdminInstructorDetail />} />
                                <Route path="subscriptions" element={<Navigate to="/admin" replace />} />
                                <Route path="dunning" element={<AdminDunning />} />
                                <Route path="analytics" element={<AdminAnalytics />} />
                                <Route path="health" element={<AdminHealth />} />
                                <Route path="subscription-pricing" element={<AdminSubscriptionPricing />} />
                                <Route path="webhooks" element={<AdminStripeWebhooks />} />
                                <Route path="discount-codes" element={<AdminDiscountCodes />} />
                                <Route path="testimonials" element={<AdminTestimonials />} />
                                <Route path="blog" element={<AdminBlog />} />
                                <Route path="cancellation-reasons" element={<AdminCancellationReasons />} />
                                <Route path="audit-log" element={<AdminAuditLog />} />
                                <Route path="user-audit" element={<Navigate to="/admin/dashboard" replace />} />
                                <Route path="email-deliverability" element={<Navigate to="/admin/mass-email" replace />} />
                                <Route path="mass-email" element={<AdminMassEmail />} />
                                <Route path="security" element={<AdminSecurity />} />
                                <Route path="feature-flags" element={<AdminFeatureFlags />} />
                                <Route path="payment-heal" element={<AdminPaymentHeal />} />
                                <Route path="ai-assistant" element={<AdminAIAssistant />} />
                                <Route path="fraud" element={<AdminFraudReview />} />
                            </Route>

                            <Route path="/claim-family/:token" element={<ClaimFamily />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </Suspense>
                    </LocaleProvider>
                </BrowserRouter>
                </FeatureFlagProvider>
                </AuthProvider>
        </div>
    );
}

export default App;
